import { useQuery } from "@tanstack/react-query";
import api from "./api";
import type { NotificationType, NotificationResponse } from "@/interfaces";
import { useUserStore } from "@/stores/userStore";

/**
 * Notification service for managing user notifications
 */

// API methods
export const notificationService = {
  /**
   * Get paginated notifications for the current user
   * @param companyId - Optional company filter (used for Admin/Employee, ignored for Finance)
   */
  getNotifications: async (
    page: number = 1,
    limit: number = 10,
    isRead?: boolean,
    companyId?: string,
  ): Promise<NotificationResponse> => {
    try {
      const params: Record<string, number | boolean | string> = { page, limit };
      if (isRead !== undefined) {
        params.isRead = isRead;
      }
      if (companyId) {
        params.companyId = companyId;
      }
      const response = await api.get("/notifications", { params });
      return response.data;
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
      throw new Error("Failed to load notifications. Please try again.");
    }
  },

  /**
   * Get unread notification count
   * @param companyId - Optional company filter (used for Admin/Employee, ignored for Finance)
   */
  getUnreadCount: async (companyId?: string): Promise<{ unreadCount: number }> => {
    try {
      const params: Record<string, string> = {};
      if (companyId) {
        params.companyId = companyId;
      }
      const response = await api.get("/notifications/unread-count", { params });
      return response.data;
    } catch (error) {
      console.error("Failed to fetch unread count:", error);
      throw new Error("Failed to load notification count.");
    }
  },

  /**
   * Mark a notification as read
   */
  markAsRead: async (notificationId: string): Promise<{ message: string; notification: NotificationType }> => {
    try {
      const response = await api.put(`/notifications/${notificationId}/read`);
      return response.data;
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
      throw new Error("Failed to mark notification as read.");
    }
  },

  /**
   * Mark all notifications as read
   */
  markAllAsRead: async (): Promise<{ message: string; count: number }> => {
    try {
      const response = await api.put("/notifications/mark-all-read");
      return response.data;
    } catch (error) {
      console.error("Failed to mark all as read:", error);
      throw new Error("Failed to mark all notifications as read.");
    }
  },

  /**
   * Delete a notification
   */
  deleteNotification: async (notificationId: string): Promise<{ message: string }> => {
    try {
      const response = await api.delete(`/notifications/${notificationId}`);
      return response.data;
    } catch (error) {
      console.error("Failed to delete notification:", error);
      throw new Error("Failed to delete notification.");
    }
  },
};

// React Query hooks

/**
 * Hook to fetch notifications with pagination and filtering
 * Note: companyId filters notifications - required for Admin/Employee, optional for Finance
 */
export const useNotifications = (
  page: number = 1,
  limit: number = 10,
  isRead?: boolean,
  companyId?: string,
  enabled: boolean = true,
) => {
  const user = useUserStore((state) => state.user);

  return useQuery({
    queryKey: ["notifications", companyId, page, limit, isRead],
    queryFn: () => notificationService.getNotifications(page, limit, isRead, companyId),
    staleTime: 30000, // 30 seconds
    enabled: !!user && enabled, // Only check user authentication, not companyId
  });
};

/**
 * Hook to fetch unread notification count
 * Note: companyId filters notifications - required for Admin/Employee, optional for Finance
 */
export const useUnreadCount = (companyId?: string) => {
  const user = useUserStore((state) => state.user);

  return useQuery({
    queryKey: ["notifications", "unread-count", companyId],
    queryFn: () => notificationService.getUnreadCount(companyId),
    staleTime: 30000, // 30 seconds
    refetchInterval: 60000, // Refetch every minute
    enabled: !!user, // Only check user authentication, not companyId
  });
};
